import React from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dispatch, SetStateAction } from 'react';

import * as z from 'zod';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Slider } from '@/components/ui-kit/slider';
import { InfoTooltip } from '@/components/ui-kit/info-tool-tip';
import { Trash2 } from 'lucide-react';
import { transformToUniversal } from '@/modules/gpt-chats/utils/aimodel-form.utils';
import { useCreateModel } from '@/modules/gpt-chats/hooks/use-aimodel';
import { useToast } from '@/hooks/use-toast';

const CustomAddKeyFormSchema = z.object({
  model: z.string().trim().min(1, 'Model name is required'),
  providerName: z.string().trim().min(1, 'Provider name is required'),
  url: z.string().trim().url('Must be a valid URL'),
  apiKey: z.string().min(1, 'API key is required'),
  apiVersion: z.string().trim().min(1, 'API version is required'),
  DefaultTemp: z.number().min(0).max(2),
  MaxTokens: z.number().min(1).max(32768),
  customHeaders: z
    .array(
      z.object({
        key: z.string().trim().optional().nullable(),
        value: z.string().trim().optional().nullable(),
      })
    )
    .default([]),
});
type FormSchema = z.infer<typeof CustomAddKeyFormSchema>;

const CustomAddKeyFormDefaultValue: FormSchema = {
  model: '',
  providerName: 'Custom',
  url: '',
  apiKey: '',
  apiVersion: '',
  DefaultTemp: 0.3,
  MaxTokens: 10922,
  customHeaders: [
    { key: '', value: '' },
    { key: '', value: '' },
  ],
};

interface CustomModelAddKeyModalProps {
  addKeyModalOpen: boolean;
  setAddKeyModalOpen: Dispatch<SetStateAction<boolean>>;
}

import { useTranslation } from 'react-i18next';

export const CustomModelAddKeyModal = ({
  addKeyModalOpen,
  setAddKeyModalOpen,
}: CustomModelAddKeyModalProps) => {
  const { t } = useTranslation();
  const provider = 'CUSTOM';
  const { mutateAsync, isPending } = useCreateModel();
  const { toast } = useToast();

  const form = useForm<FormSchema>({
    defaultValues: CustomAddKeyFormDefaultValue,
    resolver: zodResolver(CustomAddKeyFormSchema),
    mode: 'onChange',
  });

  const { control, handleSubmit } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customHeaders' as const,
  });

  const onSubmitHandler = async (data: FormSchema) => {
    try {
      const headersObject = Object.fromEntries(
        (data.customHeaders || []).filter((h) => h.key && h.value).map((h) => [h.key!, h.value!])
      );

      const payload = transformToUniversal(provider.toLowerCase(), {
        ...data,
        customHeaders: headersObject, // FIXED
      });

      payload.model_name = payload.model_name.trim() ?? 'Custom Model';
      payload.display_name = payload.model_name;

      const res = await mutateAsync(payload);

      if (res.is_success) {
        toast({ description: t('MODEL_ADDED_SUCCESSFULLY'), variant: 'success' });
      } else {
        toast.error(res.detail);
      }

      setAddKeyModalOpen(false);
      form.reset();
    } catch (err) {
      toast.error({ errors: err instanceof Error ? err.message : String(err) });
    }
  };

  return (
    <Dialog
      open={addKeyModalOpen}
      onOpenChange={(open) => {
        setAddKeyModalOpen(open);
        if (!open) form.reset(CustomAddKeyFormDefaultValue);
      }}
    >
      <DialogContent className="w-[calc(100%-2rem)] p-6 sm:mx-0 sm:w-[720px]">
        <DialogHeader>
          <DialogTitle>{t('ADD_CUSTOM_MODEL')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmitHandler)}
            className="-mr-3 max-h-[80vh] space-y-6 overflow-y-auto pl-1 pr-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar]:w-1.5"
          >
            {/* Model Name */}
            <FormField
              control={control}
              name="model"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('NAME_LABEL')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t('ENTER_MODEL_NAME_PLACEHOLDER')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="rounded border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-blue-600 dark:bg-blue-950/60 dark:text-blue-200">
              {t('CONNECT_MODEL_OPENAI_COMPATIBLE_API')}
            </div>

            {/* URL */}
            <FormField
              control={control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('API_URL_LABEL')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t('ENTER_API_URL_PLACEHOLDER')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* API Key */}
            <FormField
              control={control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('API_KEY')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('ENTER_API_KEY_PLACEHOLDER')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="apiVersion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('API_VERSION_LABEL')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t('API_VERSION_PLACEHOLDER')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="DefaultTemp"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel className="flex items-center gap-2 text-base font-medium">
                      {t('TEMPERATURE_LABEL')}
                      <InfoTooltip content={t('TEMPERATURE_DESCRIPTION')} />
                    </FormLabel>
                  </div>

                  <div className="mt-2 flex items-center gap-4">
                    <Slider
                      min={0}
                      max={2}
                      step={0.01}
                      value={[field.value ?? 0.3]}
                      onValueChange={(values: number[]) => field.onChange(values[0])}
                      className="flex-1"
                    />
                    <div className="flex min-w-[80px] items-center gap-1 rounded-lg border bg-background px-2 py-1">
                      <span className="font-bold text-high-emphasis">
                        {(field.value ?? 0.3).toFixed(2)}
                      </span>
                      <span className="text-medium-emphasis">/ 2</span>
                    </div>
                  </div>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="MaxTokens"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel className="flex items-center gap-2 text-base font-medium">
                      {t('MAXIMUM_TOKENS_LABEL')}
                      <InfoTooltip content={t('MAXIMUM_TOKENS_DESCRIPTION')} />
                    </FormLabel>
                  </div>

                  <div className="mt-2 flex items-center gap-4">
                    <Slider
                      min={1}
                      max={32768}
                      step={1}
                      value={[field.value ?? 10922]}
                      onValueChange={(values: number[]) => field.onChange(values[0])}
                      className="flex-1"
                    />
                    <div className="flex min-w-[100px] items-center gap-1 rounded-lg border bg-background px-2 py-1">
                      <span className="font-bold text-high-emphasis">{field.value ?? 10922}</span>
                      <span className="text-medium-emphasis">/ 32768</span>
                    </div>
                  </div>

                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <h4 className="text-sm font-medium">{t('CUSTOM_HEADERS_OPTIONAL')}</h4>

              <div className="mt-3 space-y-2">
                {fields.map((f, idx) => (
                  <div key={f.id} className="flex items-center gap-3">
                    <FormField
                      control={control}
                      name={`customHeaders.${idx}.key` as const}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input
                              placeholder={t('HEADER_KEY')}
                              value={field.value ?? ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              name={field.name}
                              ref={field.ref}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name={`customHeaders.${idx}.value` as const}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            {/* <Input placeholder="Enter value" {...field} /> */}
                            <Input
                              placeholder={t('VALUE')}
                              value={field.value ?? ''}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              name={field.name}
                              ref={field.ref}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(idx)}
                      className="text-medium-emphasis hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => append({ key: '', value: '' })}
                    className="mt-2"
                  >
                    {t('ADD_BUTTON')}
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter className="sticky bottom-0 left-0 right-0 mt-4 flex items-center justify-end gap-3 border-t bg-background py-4">
              <DialogClose asChild>
                <Button variant="outline" type="button" onClick={() => setAddKeyModalOpen(false)}>
                  {t('CANCEL')}
                </Button>
              </DialogClose>
              <Button disabled={!form.formState.isValid || isPending} type="submit">
                {isPending ? t('SAVING') : t('SAVE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomModelAddKeyModal;
